import { useCallback } from "react";
import { has, omit } from "lodash";

import KEY_CODES from "../Utils/keyCodes";
import { useButtonProps } from "./useButtonProps";

// This component is used in place of a <Button> component, when the contents of the
// generated <button> tag would include <div> tags, and making it spans would be too
// complicated.
//
// This is a "div button" which is accessible by disabled users and compatible with both
// W3C HTML validation and ARIA/WCAG standards.
//
const ButtonDiv = props => {
  const { disabled } = props;
  const {
    passthroughProps: inhertiedPassthroughProps,
    className,
    content,
    isActive
  } = useButtonProps(props);

  const passthroughProps = omit(inhertiedPassthroughProps, "disabled");
  const { onClick } = passthroughProps;

  if (!has(passthroughProps, "role")) {
    passthroughProps.role = disabled ? undefined : "button";
  }

  if (!has(passthroughProps, "tabIndex")) {
    passthroughProps.tabIndex = disabled ? undefined : "0";
  }

  // To be keyboard-navigation compatible, since the button is a <div> and not an actual
  // button, we need to handle the Enter and Space keys and ensure they trigger the
  // "click" of the element.
  const handleKeyDown = useCallback(
    event => {
      if (event.keyCode !== KEY_CODES.ENTER && event.keyCode !== KEY_CODES.SPACE) return;

      event.preventDefault();
      onClick(event);
    },
    [onClick]
  );

  const isPressed =
    (isActive === true && "true") || (isActive === false && "false") || null;

  return (
    <div
      {...passthroughProps}
      className={className}
      onKeyDown={handleKeyDown}
      aria-pressed={isPressed}
    >
      {content}
    </div>
  );
};

export default ButtonDiv;
